/**
 * Collection Workflow Tab for NCR Edit
 *
 * Manages the collection process workflow including status stepper,
 * initiation form, and status progression actions.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Package, Clock, Truck, CheckCircle2, Loader2,
} from 'lucide-react';
import type { NonConformanceReport as NCRType } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

// =========================================================================
// Collection Workflow Status Stepper
//
// CR-5959 R4: stepper shows the 4 SP-parity states (Requested / Pending /
// Confirmed / Completed). The customer's "Move to Pending" button is
// removed (no manual hop). Customer Initiate auto-lands the process at
// 'pending', so new rows never observably sit at 'requested' — the
// Requested node displays as completed the moment the process exists.
// Legacy / SP-imported rows that hold 'requested' continue to render
// correctly.
// =========================================================================

const COLLECTION_STEPS = [
  { key: 'requested', label: 'Requested', icon: Clock },
  { key: 'pending', label: 'Pending', icon: Package },
  { key: 'confirmed', label: 'Confirmed', icon: Truck },
  { key: 'completed', label: 'Completed', icon: CheckCircle2 },
] as const;

function CollectionStatusStepper({ status }: { status: string }) {
  const currentIdx = COLLECTION_STEPS.findIndex(s => s.key === status);
  return (
    <div className="flex items-center gap-1 w-full">
      {COLLECTION_STEPS.map((step, idx) => {
        const Icon = step.icon;
        const isCompleted = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <div key={step.key} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium ${
                isCompleted ? 'bg-green-100 text-green-700' :
                isCurrent ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300' :
                'bg-gray-100 text-gray-400'
              }`}>
                <Icon className="h-4 w-4" />
              </div>
              <span className={`text-[10px] mt-1 ${
                isCompleted ? 'text-green-700 font-medium' :
                isCurrent ? 'text-blue-700 font-medium' :
                'text-gray-400'
              }`}>{step.label}</span>
            </div>
            {idx < COLLECTION_STEPS.length - 1 && (
              <div className={`h-0.5 flex-1 mx-1 ${
                idx < currentIdx ? 'bg-green-300' : 'bg-gray-200'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// =========================================================================
// Collection Workflow Tab
// =========================================================================

export interface CollectionWorkflowTabProps {
  ncr: NCRType;
  isCustomer: boolean;
  isSupplier: boolean;
  saving: boolean;
  onInitiate: (data: { proposed_collection_date?: string; collection_address?: string; collection_notes?: string; customer_notes?: string }) => void;
  onUpdateStatus: (status: string, data?: Record<string, unknown>) => void;
}

export function CollectionWorkflowTab({ ncr, isCustomer, isSupplier, saving, onInitiate, onUpdateStatus }: CollectionWorkflowTabProps) {
  const [initForm, setInitForm] = useState({ proposed_collection_date: '', collection_address: '', customer_notes: '' });
  const { formatDate } = useFormatters();
  const [confirmForm, setConfirmForm] = useState({ confirmed_collection_date: '', tracking_reference: '' });
  // CR-6073: Proposed Collection Date is required — block submit and show a
  // field error rather than firing a request the backend rejects (a blank
  // date previously produced a silent 500).
  const [initDateError, setInitDateError] = useState<string | null>(null);

  const handleInitiate = () => {
    if (!initForm.proposed_collection_date) {
      setInitDateError('Please enter a proposed collection date.');
      return;
    }
    setInitDateError(null);
    onInitiate(initForm);
  };
  const [completionNotes, setCompletionNotes] = useState('');

  const process = ncr.collection_process;

  if (!ncr.is_collection_required) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8 text-muted-foreground">
            Collection is not required for this NCR.
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no process exists yet, show initiation form
  if (!process) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Initiate Collection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Defective parts need to be collected/returned. Start the collection process below.
          </p>
          {isCustomer ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Proposed Collection Date <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={initForm.proposed_collection_date}
                    onChange={e => {
                      const v = e.target.value;
                      setInitForm(f => ({ ...f, proposed_collection_date: v }));
                      if (v) setInitDateError(null);
                    }}
                    aria-invalid={!!initDateError}
                  />
                  {initDateError && <p className="text-sm text-red-500">{initDateError}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Collection Address</Label>
                  <Input
                    value={initForm.collection_address}
                    onChange={e => setInitForm(f => ({ ...f, collection_address: e.target.value }))}
                    placeholder="Where should parts be collected from?"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={initForm.customer_notes}
                  onChange={e => setInitForm(f => ({ ...f, customer_notes: e.target.value }))}
                  placeholder="Instructions for the supplier..."
                  rows={3}
                />
              </div>
              <Button onClick={handleInitiate} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Package className="h-4 w-4 mr-2" />}
                Initiate Collection
              </Button>
            </>
          ) : (
            <p className="text-sm text-amber-600">Waiting for the customer to initiate the collection process.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Process exists - show status stepper and actions
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Collection Process
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <CollectionStatusStepper status={process.status} />

        {/* Process details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Requested by:</span>{' '}
            <span className="font-medium">{process.requested_by_name}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Requested:</span>{' '}
            <span className="font-medium">{formatDate(process.requested_date)}</span>
          </div>
          {process.proposed_collection_date && (
            <div>
              <span className="text-muted-foreground">Proposed date:</span>{' '}
              <span className="font-medium">{formatDate(process.proposed_collection_date)}</span>
            </div>
          )}
          {process.confirmed_collection_date && (
            <div>
              <span className="text-muted-foreground">Confirmed date:</span>{' '}
              <span className="font-medium">{formatDate(process.confirmed_collection_date)}</span>
            </div>
          )}
          {process.tracking_reference && (
            <div>
              <span className="text-muted-foreground">Tracking:</span>{' '}
              <span className="font-medium">{process.tracking_reference}</span>
            </div>
          )}
          {process.actual_collection_date && (
            <div>
              <span className="text-muted-foreground">Actual collection:</span>{' '}
              <span className="font-medium">{formatDate(process.actual_collection_date)}</span>
            </div>
          )}
          {process.collection_address && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Address:</span>{' '}
              <span className="font-medium">{process.collection_address}</span>
            </div>
          )}
        </div>

        {/* Notes display */}
        {(process.customer_notes || process.collection_notes || process.supplier_receipt_notes) && (
          <div className="space-y-2 border-t pt-4">
            {process.customer_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Customer notes:</span> {process.customer_notes}</div>
            )}
            {process.collection_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Collection notes:</span> {process.collection_notes}</div>
            )}
            {process.supplier_receipt_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Supplier receipt notes:</span> {process.supplier_receipt_notes}</div>
            )}
          </div>
        )}

        {/* Actions based on current status */}
        {/* CR-5959 R4: supplier confirms at 'pending' — this is the
            original Helix gate (Amber 17:19, 02/06). Customer's Initiate
            auto-lands the row at 'pending' so this unlocks immediately
            after the customer kicks off collection. */}
        {process.status === 'pending' && isSupplier && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Confirm Collection Date</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Confirmed Collection Date</Label>
                <Input
                  type="date"
                  value={confirmForm.confirmed_collection_date}
                  onChange={e => setConfirmForm(f => ({ ...f, confirmed_collection_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Tracking Reference</Label>
                <Input
                  value={confirmForm.tracking_reference}
                  onChange={e => setConfirmForm(f => ({ ...f, tracking_reference: e.target.value }))}
                  placeholder="Shipment tracking number"
                />
              </div>
            </div>
            <Button onClick={() => onUpdateStatus('confirmed', confirmForm)} disabled={saving}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Confirm Date
            </Button>
          </div>
        )}

        {process.status === 'confirmed' && isCustomer && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Complete Collection</h4>
            <div className="space-y-2">
              <Label>Receipt Notes (optional)</Label>
              <Textarea
                value={completionNotes}
                onChange={e => setCompletionNotes(e.target.value)}
                placeholder="Confirm parts received in good order..."
                rows={2}
              />
            </div>
            <Button onClick={() => onUpdateStatus('completed', { supplier_receipt_notes: completionNotes })} disabled={saving}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Mark as Completed
            </Button>
          </div>
        )}

        {process.status === 'completed' && (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <h3 className="font-semibold text-green-800">Collection Completed</h3>
              {process.completed_date && (
                <p className="text-sm text-green-600">
                  Completed on {formatDate(process.completed_date)}
                  {process.completed_by_name && ` by ${process.completed_by_name}`}
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
